<template>
  <div class="module-staff-report">
    <div>
      <h3 class="text-2xl font-semibold text-gray-900 mb-4">
        Response Data
      </h3>
      <div class="mt-2">
        <div v-if="loading" class="text-center py-4">
          <div class="spinner"></div>
          <p class="mt-2 text-gray-600">Loading surveys...</p>
        </div>
        <div v-else-if="error" class="text-red-500 text-center py-4">
          {{ error }}
        </div>
        <div v-else-if="surveys.length === 0" class="text-center py-4">
          <p class="text-gray-600">No surveys available.</p>
        </div>
        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 shadow-sm rounded-lg overflow-hidden">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Survey Title
                </th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Survey Type
                </th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="survey in paginatedSurveys" :key="survey.id"
                class="hover:bg-gray-50 transition-colors duration-200">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">
                    {{ survey.title }} 
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">
                     {{ survey.type }} - {{ survey.department }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button @click="downloadSurveyResults(survey.id)" :disabled="downloadingResults"
                    class="text-indigo-600 hover:text-indigo-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
                    <span v-if="downloadingResults">Downloading...</span>
                    <span v-else>Download</span>
                  </button>

                  <button @click="downloadSurveyResults(survey.id, true)" :disabled="downloadingResults"
                    v-if="userRole == 'admin'"
                    class="text-indigo-600 ml-2 hover:text-indigo-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
                    <span v-if="downloadingResults">Downloading...</span>
                    <span v-else>DownloadFull</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="py-3 flex items-center justify-between border-t border-gray-200 bg-white px-4 sm:px-6">
            <div class="flex-1 flex justify-between sm:hidden">
              <button @click="prevPage" :disabled="currentPage === 1"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                Previous
              </button>
              <button @click="nextPage" :disabled="currentPage === totalPages"
                class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                Next
              </button>
            </div>
            <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p class="text-sm text-gray-700">
                  Showing
                  <span class="font-medium">{{ startIndex + 1 }}</span>
                  to
                  <span class="font-medium">{{ endIndex }}</span>
                  of
                  <span class="font-medium">{{ surveys.length }}</span>
                  results
                </p>
              </div>
              <div>
                <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button @click="prevPage" :disabled="currentPage === 1"
                    class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                    <span class="sr-only">Previous</span>
                    <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
                      aria-hidden="true">
                      <path fill-rule="evenodd"
                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                        clip-rule="evenodd" />
                    </svg>
                  </button>
                  <button v-for="page in displayedPages" :key="page" @click="goToPage(page)" :class="[
                    page === currentPage
                      ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors duration-200',
                  ]">
                    {{ page }}
                  </button>
                  <button @click="nextPage" :disabled="currentPage === totalPages"
                    class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                    <span class="sr-only">Next</span>
                    <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
                      aria-hidden="true">
                      <path fill-rule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clip-rule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!--  <div class="items-center px-4 py-3">
        <button
          @click="$emit('close')"
          class="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors duration-200"
        >
          Close
        </button>
      </div>-->
    </div>
  </div>
</template>

<script>
import { surveyApi } from "@/api/surveyManagement";
import { mapState } from "vuex";

export default {
  name: "DownloadSurveyResultsModal",
  data() {
    return {
      surveys: [],
      loading: true,
      error: null,
      downloadingResults: false,
      currentPage: 1,
      pageSize: 10,
    };
  },
  computed: {
    ...mapState({
      user: state=> state.auth.user,
      schools: state => state.schools,
      departments: state => state.departments,
      userRole: state => state.auth.userRole,
      userSchool: state => state.auth.userSchool,
      userDepartment: state => state.auth.userDepartment,
    }),
    availableDepartments() {
      if (this.userRole == 'admin' || this.userRole == 'supervisor')
        return [...this.schools.map(d => d.fullName), ...this.departments.map(d => d.fullName)];
      if (this.userRole == 'school_admin') {
        let school = this.schools.find(school => school.fullName === this.userSchool);
        let _userDepartment = this.departments.filter(
          department => department.school === school.shortName
        ).map(d=>d.fullName);
        return [school.fullName, ..._userDepartment];
      }
      else if (this.user.groups.length > 0)
      {
        let _d = this.departments.filter(
          department => department.fullName === this.user.groups[0].identity
        ).map(d=>d.fullName);
        
        let _s = this.schools.filter(school => school.fullName == this.user.groups[0].identity).map(o=>o.fullName)
        
        if (_d.length == 0 && _s.length > 0)
        {
          console.log(_s)
          let _school = this.schools.find(x=>x.fullName == _s[0]).shortName
          console.log(this.departments)
          _d = this.departments.filter(x=>x.school == _school).map(d=>d.fullName);
          console.log(_d)
        }

        return _d.concat(_s)
      }
      else
        return this.departments.filter(
          department => department.fullName === this.userDepartment
        ).map(d=>d.fullName);
    },
    totalPages() {
      return Math.ceil(this.surveys.length / this.pageSize);
    },
    paginatedSurveys() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.surveys.slice(start, end);
    },
    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },
    endIndex() {
      return Math.min(this.startIndex + this.pageSize, this.surveys.length);
    },
    displayedPages() {
      const range = 2;
      const pages = [];
      for (
        let i = Math.max(1, this.currentPage - range);
        i <= Math.min(this.totalPages, this.currentPage + range);
        i++
      ) {
        pages.push(i);
      }
      return pages;
    },
  },
  mounted() {
    this.fetchSurveys();
  },
  methods: {
    async fetchSurveys() {
      try {
        this.loading = true;
        let _surveys = await surveyApi.getAll();

        _surveys = _surveys.filter((survey) => survey.status == 'Published');
       // _surveys = _surveys.filter((survey => survey.type == 'University' && survey.released) || this.availableDepartments.includes(survey.department));
        if (this.userRole != 'admin')
        {
          let _uni = this.user.groups.length > 0 ? _surveys.filter(survey=> survey.type == 'University' && survey.released) : []; //_surveys.filter(survey=> survey.type == 'University' && survey.released);
          let _dep = _surveys.filter(survey=>survey.type=='School/Department' && this.availableDepartments.includes(survey.department))
          this.surveys = _uni.concat(_dep) 
        }
          //this.surveys = _surveys.filter(survey =>!(survey.type == 'University' && !survey.released));
        else
          this.surveys = _surveys;
      } catch (error) {
        console.error("Error fetching surveys:", error);
        this.error = "Failed to load surveys. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    async downloadSurveyResults(surveyId, isAdmin = false) {
      let blob = await surveyApi.downloadSurveyResponse(surveyId, isAdmin);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SurveyResult-${surveyId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    goToPage(page) {
      this.currentPage = page;
    },
  },
};
</script>

<style scoped>
.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>